import router from "@/router";
import logger from "@/utils/logger";
import { HttpClient } from "@/utils/http-client";
import {
  InvoiceService,
  OnboardingService,
  PayerService,
  PaymentService,
  TermService,
  UserService,
  MerchantService,
} from "@/services";
import { format } from "date-fns";

export default {
  scrollToMyDetails() {
    let myDetails = document.getElementById("myDetails");
    let el = document.getElementById("main-wrapper");

    el.scrollTo({
      top: myDetails.offsetTop - 30,
      left: 0,
      behavior: "smooth",
    });
  },

  async getUserPassword({ state, commit }) {
    const data = {
      TransactionId: state.transactionId,
      BillingDetails: {
        Email: state.payer.email,
      },
    };

    try {
      const response = await UserService.generatePasswordCreationUrl(data);
      commit("setUserPassword", response);
    } catch (error) {
      logger.error(error);
    }
  },

  async login({ dispatch }, payload) {
    await dispatch("updateUserDetails", payload);
    await dispatch("getPayerDetails");
  },

  async saveJWTToken({ commit }, token) {
    commit("saveJWT", token);
  },

  async updateUserDetails({ commit, dispatch }, payload) {
    await dispatch("saveJWTToken", payload.token);
    commit("userLogin", {
      username: payload.username,
      name: payload.name,
      email: payload.email,
      role: payload.role,
    });
  },

  async getUserDetails({ state, dispatch }, transactionId) {
    try {
      const requestAuthResponse = await UserService.getSession(transactionId);
      if (
        "termsThatRequireAcceptance" in requestAuthResponse &&
        requestAuthResponse.termsThatRequireAcceptance.length > 0
      ) {
        state.requestAuthResponseBodyForTermsAcceptance = requestAuthResponse;
      } else {
        state.requestAuthResponseBodyForTermsAcceptance = {};
        if (requestAuthResponse.token && requestAuthResponse.token !== "null") {
          await dispatch("updateUserDetails", requestAuthResponse);
        }
      }
    } catch (error) {
      logger.error(error);
    }
  },

  async getMerchantPayerCompaniesForVirtualTerminal({ commit, state }) {
    try {
      state.isMerchantPayerCompaniesApiLoading = true;
      const merchantPayerResponse = await MerchantService.getMerchantPayerCompanies(state.transactionId);
      commit("setMerchantPayerCompanies", merchantPayerResponse);
      state.isMerchantPayerCompaniesApiLoading = false;
    } catch (error) {
      state.isMerchantPayerCompaniesApiLoading = false;
      logger.error(error);
    }
  },

  async extendUserSession({ state, dispatch }) {
    try {
      const response = await UserService.getSession(state.transactionId);
      if (response && response.token && response.token !== "null") {
        await dispatch("saveJWTToken", response.token);
      }
    } catch (error) {
      logger.error(error);
    }
  },

  updateDefaultPaymentMethod({ getters, commit, state }) {
    const defaultPaymentMethods = state.payer.paymentMethods.filter(
      (paymentMethod) =>
        getters.isPaymentMethodAccepted(paymentMethod) && paymentMethod.companyName === state.invoice.payerCompany
    );
    if (defaultPaymentMethods.length) {
      // Default payment method or the first one
      const defaultPaymentMethod =
        defaultPaymentMethods.find((paymentMethod) => paymentMethod.isDefault) || defaultPaymentMethods[0];
      commit("selectPaymentMethod", defaultPaymentMethod.id);
    } else {
      commit("selectPaymentMethod", null);
    }
  },

  async getPayerPaymentMethods({ dispatch, commit, state }) {
    try {
      commit("setLoadingPaymentMethods", true);
      const response = await PayerService.getPaymentMethods(state.payer.userId, state.transactionId);
      //add the cards to store
      commit("setPayerPaymentMethods", response);

      dispatch("updateDefaultPaymentMethod");
    } catch (error) {
      logger.error(error);
      commit("setExtraDataLoadingError", true);
    } finally {
      commit("setLoadingPaymentMethods", false);
    }
  },

  async getPayerDetails({ state, commit, dispatch }) {
    try {
      commit("setLoadingUserDetails", true);
      commit("clearPayerDetails");
      const response = await PayerService.getPayerDetails(state.transactionId);
      if (response && response.length) {
        if (state.user.isAdminRole) {
          commit("setPayersList", response);
        } else {
          commit("setPayerDetails", response[0]);
          if (state.payer.userId) dispatch("getPayerPaymentMethods");
        }
      }
    } catch (error) {
      logger.error(error, error.response);
      commit("setExtraDataLoadingError", true);
    } finally {
      commit("setLoadingUserDetails", false);
    }
  },

  async getMerchantTandC({ state, commit }, consultingServiceIds) {
    try {
      const response = await TermService.getMerchantTerms(state.transactionId, consultingServiceIds);
      commit("setTermsAndConditions", response);
    } catch (error) {
      logger.error(error, error.response);
      commit("setExtraDataLoadingError", true);
    }
  },

  async getTermsDetails({ commit }, terms) {
    try {
      commit("setLoadingTerms", true);
      await Promise.all(
        terms.map(async (term) => {
          const termData = await HttpClient.get(term.endpoint);
          commit("updateTermDetails", termData);
        })
      );
    } catch (error) {
      logger.error(error);
      commit("setExtraDataLoadingError", true);
    } finally {
      commit("setLoadingTerms", false);
    }
  },

  async getInvoiceDetails({ commit, dispatch, state }, transactionId) {
    commit("setTransactionId", transactionId);
    try {
      commit("setLoadingInvoiceDetails", true);
      const response = await InvoiceService.getInvoice(state.transactionId);

      //check if npe is enabled in config
      dispatch("checkIfNpeEnabled", response);
      commit("transactionDataInit", response);
      commit("setTermsAndConditions", response.termsAndConditions);

      // Set the page title for every page except Virtual Terminal
      if (router.currentRoute.name !== "TransactionSearch") {
        dispatch("setPageTitle");
      }
    } catch (error) {
      if (error.response.data) {
        commit("transactionError", error.response.data.Message);
      } else {
        commit("transactionError", "Error fetching invoice details.");
      }
    } finally {
      commit("setLoadingInvoiceDetails", false);
    }
  },

  async generateNewTransactionId({ state }, payload) {
    const response = await InvoiceService.generateTransactionId({
      ...payload.requestBody,
      TransactionId: state.transactionId,
    });
    if (response.transactionId) {
      await router.push({
        path: payload.redirectPath,
        query: { transactionId: response.transactionId },
      });
    } else {
      this.errorMessage = "Error generating transaction ID.";
    }
  },

  checkIfInvoiceIsSynced({ commit }, payload) {
    if (payload.invoiceDetails == undefined) {
      //checks if invoice is synced - if not will log user out and go into prepayment // guest flow
      commit("logoutUser");
      commit("continueAsGuest");
    }
  },

  setPageTitle({ state, getters }) {
    document.title = `Wise-Pay | Invoice: ${state.invoice.invoiceNumber} - Amount Due: ${getters.invoiceAmountDue}`;
  },

  checkIfNpeEnabled({ state }, payload) {
    state.isNpeEnabled = payload.isNewPaymentExperienceEnabled;

    //TODO turn on redirects
    //redirect user if NPE is disbled
    if (!payload.isNewPaymentExperienceEnabled && payload.isNewPaymentExperienceEnabled !== undefined) {
      window.location.href = payload.oldPaymentExperienceUrl;
    }
  },

  // all guest payments are processed in the function below
  async guestPayment({ state, getters, commit }, acceptedTerms) {
    const data = {
      TransactionId: state.transactionId,
      TokenFromCardProvider: getters.selectedPaymentMethod.token,
      MaskedCreditCard: {
        cardName: getters.selectedPaymentMethod.cardName,
        cardNumber: getters.selectedPaymentMethod.maskedCardNumber,
        cardExpiry: getters.selectedPaymentMethod.cardExpiry,
        cardType: getters.selectedPaymentMethod.cardType,
      },
      PaymentAmount: {
        TransactionAmount: state.payingToday,
        CardType: getters.selectedPaymentMethod.cardType,
        SurchargeAmount: getters.totalSurcharge,
        IsOverrideSurcharge: state.merchant.overrideSurcharge,
      },
      AcceptedTermsAndConditions: acceptedTerms.map((term) => term.getPayload()),
      BillingDetails: {},
      PayerUserId: state.payer.userId,
    };

    //if avs is enabled push billing information for the card to config;
    if (state.merchant.isAvsEnabled && getters.selectedPaymentMethod.isCreditCard) {
      data.BillingDetails.Country = getters.selectedPaymentMethod.billingDetails.country;
      data.BillingDetails.State = getters.selectedPaymentMethod.billingDetails.state;
      data.BillingDetails.Suburb = getters.selectedPaymentMethod.billingDetails.suburb;
      data.BillingDetails.PostCode = getters.selectedPaymentMethod.billingDetails.postcode;
      data.BillingDetails.StreetAddress = getters.selectedPaymentMethod.billingDetails.streetAddress;
      data.BillingDetails.StreetAddressLineTwo = getters.selectedPaymentMethod.billingDetails.streetAddressLineTwo;
    }

    if (state.isBillingDetailsUpdated) {
      data.BillingDetails.FirstName = state.payer.firstName;
      data.BillingDetails.LastName = state.payer.lastName;
      data.BillingDetails.Company = state.payer.company;
      data.BillingDetails.PhoneNumber = state.payer.phoneNumber;
      data.BillingDetails.Email = state.payer.email;
      data.IsBillingDetailsUpdated = state.isBillingDetailsUpdated;
    }
    try {
      // Handles edge case where this method was still called even after the first attempt failed
      if (state.isPaymentEndpointFailed) {
        state.payment = {};
        commit("paymentError", "");
        return;
      }

      const response = await PaymentService.payAsGuest(data);
      state.payment = response;

      // Check and proceed if the payment is for the Third-Party Integration & `returnUrl` is present
      // Redirect to the ReturnUrl whether the payment is successful or not
      if (getters.isThirdPartyIntegrationReturnUrlPresent) {
        commit("paymentThirdPartyReturnUrl");
        return;
      }

      // This is for the normal invoices
      if (response.transactionResult?.IsSuccess) {
        commit("paymentSuccessful");
      } else {
        commit("paymentError", response.transactionResult.ErrorDetails[0].CustomErrorMessage);
      }
    } catch (error) {
      state.isPaymentEndpointFailed = true;
      state.payment = error.response.data;

      // Check and proceed if the payment is for the Third-Party Integration & `returnUrl` is present
      // Redirect to the ReturnUrl whether the payment is successful or not
      if (getters.isThirdPartyIntegrationReturnUrlPresent) {
        commit("paymentThirdPartyReturnUrl");
        return;
      }

      // This is for the normal invoices
      commit("paymentError", error.response.data.Message);
    } finally {
      commit("paymentInProgress", false);
    }
  },

  async loggedInUserPayment({ state, getters, commit }, acceptedTerms) {
    const data = {
      TransactionId: state.transactionId,
      SavedPaymentMethod: {
        Id: getters.selectedPaymentMethod.id,
        Type: getters.selectedPaymentMethod.type,
      },
      PaymentAmount: {
        TransactionAmount: state.payingToday,
        CardType: getters.selectedPaymentMethod.cardType,
        SurchargeAmount: getters.totalSurcharge,
        IsOverrideSurcharge: state.merchant.overrideSurcharge,
      },
      AcceptedTermsAndConditions: acceptedTerms.map((term) => term.getPayload()),
      BillingDetails: {},
    };

    if (state.isBillingDetailsUpdated) {
      data.BillingDetails.FirstName = state.payer.firstName;
      data.BillingDetails.LastName = state.payer.lastName;
      data.BillingDetails.Company = state.payer.company;
      data.BillingDetails.PhoneNumber = state.payer.phoneNumber;
      data.BillingDetails.Email = state.payer.email;
      data.IsBillingDetailsUpdated = state.isBillingDetailsUpdated;
    }

    //if payment is scheduled
    if (state.scheduledPaymentDate) {
      data.ScheduledPaymentDate = format(new Date(state.scheduledPaymentDate), "yyyy-MM-dd");
    }

    try {
      // Handles edge case where this method was still called even after the first attempt failed
      if (state.isPaymentEndpointFailed) {
        state.payment = {};
        commit("paymentError", "");
        return;
      }

      const response = await PaymentService.payAsLoggedInUser(state.payer.userId, data);
      state.payment = response;

      // Check and proceed if the payment is for the Third-Party Integration & `returnUrl` is present
      // Redirect to the ReturnUrl whether the payment is successful or not
      if (getters.isThirdPartyIntegrationReturnUrlPresent) {
        commit("paymentThirdPartyReturnUrl");
        return;
      }

      // This is for the normal invoices
      if (response.transactionResult?.IsSuccess) {
        commit("paymentSuccessful");
      } else {
        commit("paymentError", response.transactionResult.ErrorDetails[0].CustomErrorMessage);
      }
    } catch (error) {
      state.isPaymentEndpointFailed = true;
      state.payment = error.response.data;

      // Check and proceed if the payment is for the Third-Party Integration & `returnUrl` is present
      // Redirect to the ReturnUrl whether the payment is successful or not
      if (getters.isThirdPartyIntegrationReturnUrlPresent) {
        commit("paymentThirdPartyReturnUrl");
        return;
      }

      // This is for the normal invoices
      commit("paymentError", error.response.data.Message);
    } finally {
      commit("paymentInProgress", false);
    }
  },

  async getRequiredTermsDetails({ dispatch }, requiredTerms) {
    const unloadedTerms = requiredTerms.filter((term) => !term.isLoaded);
    await dispatch("getTermsDetails", unloadedTerms);
  },

  async getOnboardingDetails({ commit }, token) {
    try {
      commit("setOnboardingToken", token);
      commit("setLoadingOnboardingData", true);
      const response = await OnboardingService.getOnboardingDetails(token);
      commit("initializeOnboardingData", response);
      commit("setTermsAndConditions", response.termsAndConditions);
    } catch (error) {
      if (error.statusCode === 410) {
        commit("setOnboardingAsExpired");
      } else {
        commit("setLoadingOnboardingDataError", true);
      }
    } finally {
      commit("setLoadingOnboardingData", false);
    }
  },

  async getOnboardingPayerDetails({ commit, state }) {
    const response = await OnboardingService.getPayerDetails(state.onboardingToken);
    commit("setPayerDetails", {
      ...response,
      company: state.payer.company,
    });
    commit("setAutoPaymentTermsToOtherPayerStatus", response.isTermsAssignedToOtherPayer);
  },

  async acceptOnboardingTerms({ state }) {
    return OnboardingService.acceptTerms(state.onboardingToken, state.onboardingTermsId);
  },

  async logPayerOnboardingEvent({ state }, event) {
    logger.info({
      event,
      payerOnboardingToken: state.onboardingToken,
    });
  },

  async setupPayerOnboarding({ state, getters }, acceptedTerms) {
    return OnboardingService.onboardPayer(state.onboardingToken, {
      autoPaymentPaymentMethodId: getters.selectedPaymentMethod && getters.selectedPaymentMethod.id,
      acceptedTermsAndConditions: acceptedTerms.map((terms) => terms.getPayload()),
    });
  },

  async isPaymentDetailsInEdit({ commit }, payload) {
    commit("setIsPaymentDetailsInEdit", payload);
  },
};
